import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import { defineMessages, useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import { CREATE_TICKET } from '../../../gql/mutations/ticketsMutations'
import { GET_TICKET_TYPES } from '../../../gql/queries/ticketsQuery'
import { GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { GET_TICKET_ASSIGNMENT_GROUPS } from '../../../gql/queries/assignmentGroupQuery'
import { parseId } from '../../../helpers'
import { EM_DASH } from '../chatMessages'

const M = defineMessages({
  title: { id: 'inbox.ticket.title', defaultMessage: 'New ticket' },
  subject: { id: 'inbox.ticket.subject', defaultMessage: 'Subject' },
  subjectPh: { id: 'inbox.ticket.subjectPh', defaultMessage: 'What is this ticket about?' },
  description: { id: 'inbox.ticket.description', defaultMessage: 'Description' },
  type: { id: 'inbox.ticket.type', defaultMessage: 'Type' },
  selectType: { id: 'inbox.ticket.selectType', defaultMessage: 'Select type' },
  priority: { id: 'inbox.ticket.priority', defaultMessage: 'Priority' },
  low: { id: 'inbox.ticket.low', defaultMessage: 'Low' },
  medium: { id: 'inbox.ticket.medium', defaultMessage: 'Medium' },
  high: { id: 'inbox.ticket.high', defaultMessage: 'High' },
  urgent: { id: 'inbox.ticket.urgent', defaultMessage: 'Urgent' },
  assignTo: { id: 'inbox.ticket.assignTo', defaultMessage: 'Assign to' },
  user: { id: 'inbox.ticket.user', defaultMessage: 'User' },
  group: { id: 'inbox.ticket.group', defaultMessage: 'Group' },
  selectUser: { id: 'inbox.ticket.selectUser', defaultMessage: 'Select user' },
  selectGroup: { id: 'inbox.ticket.selectGroup', defaultMessage: 'Select group' },
  policy: { id: 'inbox.ticket.policy', defaultMessage: 'Related policy (optional)' },
  noPolicy: { id: 'inbox.ticket.noPolicy', defaultMessage: 'None' },
  cancel: { id: 'inbox.ticket.cancel', defaultMessage: 'Cancel' },
  create: { id: 'inbox.ticket.create', defaultMessage: 'Create ticket' },
  created: { id: 'inbox.ticket.created', defaultMessage: 'Ticket created' },
  errSubject: { id: 'inbox.ticket.errSubject', defaultMessage: 'A subject is required.' },
  errAssignee: { id: 'inbox.ticket.errAssignee', defaultMessage: 'Choose who this ticket is assigned to.' },
  errCreate: { id: 'inbox.ticket.errCreate', defaultMessage: 'Could not create the ticket.' },
})

const normalizeNodeId = (value?: string | null) => (value ? String(parseId(value)) : '')
/** Póliza sin nombre: se muestra su id (`#123`). Es un dato, no texto. */
const policyFallbackLabel = (id?: string | null) => `#${normalizeNodeId(id)}`

interface PolicyOption {
  id: string
  policyName?: string
}

export const ContactTicketModal: React.FC<{
  show: boolean
  onClose: () => void
  channel: string
  policies: PolicyOption[]
  contactName?: string | null
  onCreated?: () => void
}> = ({ show, onClose, channel, policies, contactName, onCreated }) => {
  // Sufijo del título y etiqueta de póliza sin nombre: son datos, no texto.
  const contactSuffix = contactName ? ` ${EM_DASH} ${contactName}` : ''
  const intl = useIntl()

  const [title, setTitle] = useState('')
  const [observations, setObservations] = useState('')
  const [ticketTypeId, setTicketTypeId] = useState('')
  const [priority, setPriority] = useState('MEDIUM')
  const [assignmentType, setAssignmentType] = useState<'USER' | 'GROUP'>('USER')
  const [assignedUserId, setAssignedUserId] = useState('')
  const [assignedGroupId, setAssignedGroupId] = useState('')
  const [policyId, setPolicyId] = useState('')

  const { data: dataTypes } = useQuery(GET_TICKET_TYPES, {
    variables: { isActive: true },
    fetchPolicy: 'cache-first',
  })
  const { data: dataUsers } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: { assignmentCandidates: true },
    fetchPolicy: 'cache-first',
  })
  const { data: dataGroups } = useQuery(GET_TICKET_ASSIGNMENT_GROUPS, { fetchPolicy: 'cache-first' })

  const ticketTypes = dataTypes?.ticketTypes || []
  const groups = dataGroups?.ticketAssignmentGroups || []
  const userOptions = useMemo(
    () =>
      (dataUsers?.allUsersList || []).map((u: any) => ({
        value: normalizeNodeId(u.id),
        label: `${[u.firstName, u.lastName].filter(Boolean).join(' ').trim() || 'No name'} - ${u.email || 'No email'}`,
      })),
    [dataUsers]
  )

  const [createTicket, { loading: creating }] = useMutation(CREATE_TICKET)

  const handleCreate = async () => {
    if (!title.trim()) {
      toast.warning(intl.formatMessage(M.errSubject))
      return
    }
    if (assignmentType === 'USER' && !assignedUserId) {
      toast.warning(intl.formatMessage(M.errAssignee))
      return
    }
    if (assignmentType === 'GROUP' && !assignedGroupId) {
      toast.warning(intl.formatMessage(M.errAssignee))
      return
    }
    try {
      const res: any = await createTicket({
        variables: {
          ticketData: {
            title: title.trim(),
            observations: observations.trim(),
            channel: channel || 'INTERNAL',
            externalReference: '',
            status: 'OPEN',
            priority,
            dueAt: null,
            policyId: policyId ? parseId(policyId) : null,
            ticketTypeId: ticketTypeId ? parseId(ticketTypeId) : null,
            assignmentType,
            assignedUserId: assignmentType === 'USER' && assignedUserId ? parseId(assignedUserId) : null,
            assignedGroupId: assignmentType === 'GROUP' && assignedGroupId ? parseId(assignedGroupId) : null,
          },
        },
      })
      if (!res?.data?.createTicket?.success) {
        throw new Error(res?.data?.createTicket?.errors?.[0]?.message || intl.formatMessage(M.errCreate))
      }
      toast.success(intl.formatMessage(M.created))
      onCreated?.()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.errCreate))
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered backdrop='static' keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title className='fs-5'>
          {intl.formatMessage(M.title)}
          {contactSuffix}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className='mb-3'>
          <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.subject)}</Form.Label>
          <Form.Control
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.formatMessage(M.subjectPh)}
            autoFocus
          />
        </Form.Group>

        <Form.Group className='mb-3'>
          <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.description)}</Form.Label>
          <Form.Control as='textarea' rows={3} value={observations} onChange={(e) => setObservations(e.target.value)} />
        </Form.Group>

        <Row className='mb-3'>
          <Col xs={6}>
            <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.type)}</Form.Label>
            <Form.Select value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)}>
              <option value=''>{intl.formatMessage(M.selectType)}</option>
              {ticketTypes.map((t: any) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6}>
            <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.priority)}</Form.Label>
            <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value='LOW'>{intl.formatMessage(M.low)}</option>
              <option value='MEDIUM'>{intl.formatMessage(M.medium)}</option>
              <option value='HIGH'>{intl.formatMessage(M.high)}</option>
              <option value='URGENT'>{intl.formatMessage(M.urgent)}</option>
            </Form.Select>
          </Col>
        </Row>

        <Row className='mb-3'>
          <Col xs={5}>
            <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.assignTo)}</Form.Label>
            <Form.Select
              value={assignmentType}
              onChange={(e) => {
                const v = e.target.value as 'USER' | 'GROUP'
                setAssignmentType(v)
                if (v === 'USER') setAssignedGroupId('')
                else setAssignedUserId('')
              }}
            >
              <option value='USER'>{intl.formatMessage(M.user)}</option>
              <option value='GROUP'>{intl.formatMessage(M.group)}</option>
            </Form.Select>
          </Col>
          <Col xs={7}>
            <Form.Label className='fs-8 fw-semibold text-gray-700'>&nbsp;</Form.Label>
            {assignmentType === 'USER' ? (
              <Form.Select value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)}>
                <option value=''>{intl.formatMessage(M.selectUser)}</option>
                {userOptions.map((o: { value: string; label: string }) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Select value={assignedGroupId} onChange={(e) => setAssignedGroupId(e.target.value)}>
                <option value=''>{intl.formatMessage(M.selectGroup)}</option>
                {groups.map((g: any) => (
                  <option key={g.id} value={normalizeNodeId(g.id)}>
                    {g.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </Col>
        </Row>

        {policies.length > 0 && (
          <Form.Group className='mb-1'>
            <Form.Label className='fs-8 fw-semibold text-gray-700'>{intl.formatMessage(M.policy)}</Form.Label>
            <Form.Select value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
              <option value=''>{intl.formatMessage(M.noPolicy)}</option>
              {policies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.policyName || policyFallbackLabel(p.id)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onClose} disabled={creating}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant='primary' onClick={handleCreate} disabled={creating}>
          {creating && <Spinner animation='border' size='sm' className='me-2' />}
          {intl.formatMessage(M.create)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
